import { useMemo } from 'react';

import { StoreItemImage } from '@givebacks/api/store';
import { useFormListValidator } from 'core';
import { AspectRatio, DEFAULT_PEER_PROFILE_BANNER_IMAGE, ImageMantine, Stack, Text } from 'ui';
import { useForm } from 'ui/Form';

import { BannerImageFormActions } from './BannerImageFormActions';

export type BannerImageFormType = {
  file?: File | null;
  image?: StoreItemImage;
};

const BannerImageForm = ({
  initialValue,
  onSkip,
}: {
  initialValue?: StoreItemImage;
  onSkip?: () => void;
}) => {
  const form = useForm<BannerImageFormType>({
    initialValues: {
      file: null,
      image: initialValue,
    },
  });

  const previewSrc = useMemo(() => {
    if (form.values.image) {
      return form.values.image.url;
    } else if (form.values.file) {
      const fileURL = form.values.file && URL.createObjectURL(form.values.file);
      return fileURL;
    } else {
      return undefined;
    }
  }, [form.values.file, form.values.image]);

  const isAddNewImage = useMemo(() => {
    return !initialValue && !form.values.file;
  }, [initialValue, form.values.file]);

  useFormListValidator({ form, id: 'bannerImage' });

  return (
    <Stack gap="lg">
      <Text fz="sm">
        This will appear at the top of your page and make it stand out to potential donors.
      </Text>
      <Stack gap="xs">
        <Text fz="sm" fw={600}>
          Banner Preview
        </Text>
        <AspectRatio ratio={2 / 1} pos="relative">
          <ImageMantine
            src={previewSrc}
            fallbackSrc={DEFAULT_PEER_PROFILE_BANNER_IMAGE}
            radius="lg"
            bd="1px solid cobalt.0"
          />
          {/* <ImageMantine
            src={causeLogo}
            radius={12}
            h={48}
            w={48}
            pos={'absolute'}
            top={16}
            left={16}
            style={(theme) => ({ boxShadow: theme.shadows.md })}
          /> */}
        </AspectRatio>
      </Stack>
      <BannerImageFormActions
        isAddNewImage={isAddNewImage}
        file={form.values.file}
        setFile={(file) => {
          form.setFieldValue('file', file);
        }}
        existingSrc={form.values.image}
        setExistingSrc={() => form.setFieldValue('image', undefined)}
        enableCropping
        croppedImageFileName={(file) =>
          `${file.name.split('.')[0]}-cropped.${file.type.split('/')[1]}`
        }
        onSkip={onSkip}
      />
    </Stack>
  );
};

export { BannerImageForm };
